import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAccounts, accountTypeKey } from "@/lib/AccountContext";
import { InternalTransfer } from "@/components/InternalTransfer";
import { Zap, Clock, Hash, ArrowRight, ShieldAlert, CalendarClock, CheckCircle2, Building2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, groupIban } from "@/lib/format";
import { validateIban, validateCBU, BANK_BRANDS, COUNTRY_LIST, COUNTRY_META } from "@/lib/iban";

const SEPA_INSTANT_CAP = 1000; // EUR — over this, instant auto-switches to standard

const CURRENCIES = [
  { code: "EUR", label: "Euro", symbol: "€" },
  { code: "GBP", label: "Libra esterlina", symbol: "£" },
  { code: "USD", label: "Dólar estadounidense", symbol: "$" },
  { code: "CHF", label: "Franco suizo", symbol: "CHF" },
  { code: "ARS", label: "Peso argentino", symbol: "$" },
  { code: "MXN", label: "Peso mexicano", symbol: "$" },
];

// All SEPA + SWIFT countries (sorted by SEPA first, then alphabetical)
const COUNTRIES = COUNTRY_LIST.map((c) => [c.code, `${c.flag} ${c.name}${c.sepa ? "" : " (SWIFT)"}`]);

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
    </label>
  );
}

export default function Transfers() {
  const { t } = useTranslation();
  const { accounts, selectedAccount } = useAccounts();

  const TABS = [
    { key: "sepa_instant",  label: t("transfers.tab_instant"),    icon: Zap,           desc: t("transfers.tab_instant_desc") },
    { key: "sepa_standard", label: t("transfers.tab_standard"),   icon: CalendarClock, desc: t("transfers.tab_standard_desc") },
    { key: "cbu",           label: t("transfers.tab_cbu"),        icon: Hash,          desc: t("transfers.tab_cbu_desc") },
    ...(accounts.length > 1 ? [{ key: "internal", label: t("transfers.tab_internal"), icon: ArrowLeftRight, desc: t("transfers.tab_internal_desc") }] : []),
  ];

  const [tab, setTab] = useState("sepa_instant");
  const [sourceId, setSourceId] = useState(null);
  const [form, setForm] = useState({
    beneficiary_name: "", destination: "", amount: "", concept: "", scheduled_at: "",
    currency: "EUR", destination_country: "ES",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [fx, setFx] = useState(null);

  // Source account = explicit choice or the globally-selected account.
  const sourceAccount = accounts.find((a) => a.id === sourceId) || selectedAccount || null;
  const accCurrency = (sourceAccount?.currency || "EUR").toUpperCase();
  const amtNum = parseFloat(form.amount) || 0;
  const availableBalance = sourceAccount?.balance ?? 0;
  const insufficient = amtNum > 0 && amtNum > availableBalance;

  // Default the transfer currency to the source account's base currency.
  useEffect(() => {
    if (sourceAccount) {
      setSourceId(sourceAccount.id);
      setForm((f) => (f.currency === accCurrency ? f : { ...f, currency: accCurrency }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceAccount?.id, accCurrency]);

  // Live conversion preview when the chosen currency differs from the account's.
  useEffect(() => {
    const amt = parseFloat(form.amount) || 0;
    if (!amt || !form.currency || form.currency === accCurrency) { setFx(null); return; }
    let active = true;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/fx/convert", { amount: amt, from_currency: form.currency, to_currency: accCurrency });
        if (active) setFx(data);
      } catch { if (active) setFx(null); }
    }, 400);
    return () => { active = false; clearTimeout(id); };
  }, [form.amount, form.currency, accCurrency]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // live IBAN validation when tab is SEPA
  const ibanCheck = useMemo(() => {
    if (tab === "cbu") return null;
    if (!form.destination || form.destination.replace(/\s/g, "").length < 4) return null;
    return validateIban(form.destination);
  }, [form.destination, tab]);

  // Auto-update destination_country from IBAN — as soon as the first 2 letters
  // are typed (country code), even before the IBAN is fully valid.
  React.useEffect(() => {
    if (tab === "cbu") return;
    const code = (form.destination || "").replace(/\s/g, "").slice(0, 2).toUpperCase();
    if (/^[A-Z]{2}$/.test(code) && COUNTRY_META[code] && code !== form.destination_country) {
      setForm((f) => ({ ...f, destination_country: code }));
    }
  }, [form.destination, tab, form.destination_country]);

  // Live enrichment from /api/iban/validate (which hits openiban.com — the same
  // public registry ibancalculator.com uses) to get the REAL bank name + BIC
  const [enriched, setEnriched] = useState(null);
  React.useEffect(() => {
    if (!ibanCheck?.valid) { setEnriched(null); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/iban/validate", { iban: ibanCheck.iban });
        if (alive && data?.valid) setEnriched(data);
      } catch { /* fallback to local registry — already shown */ }
    }, 250); // debounce typing
    return () => { alive = false; clearTimeout(t); };
  }, [ibanCheck?.iban, ibanCheck?.valid]);

  // Merge: if openiban returned a real name, prefer it.
  const displayBank = enriched?.bank_name || ibanCheck?.bank_name;
  const displayBic  = enriched?.bic;
  const displayCity = enriched?.city;
  const displaySource = enriched?.source; // 'openiban.com' or 'local'

  // CBU/CVU live validation with bank detection (Argentina)
  const cbuCheck = useMemo(() => {
    if (tab !== "cbu") return null;
    const clean = form.destination.replace(/[\s-]/g, "");
    if (!clean) return null;
    if (!/^\d+$/.test(clean)) return { valid: false, reason: "Solo se permiten dígitos" };
    if (clean.length < 22) return { valid: false, reason: `Faltan ${22 - clean.length} dígitos (CBU/CVU = 22)` };
    if (clean.length > 22) return { valid: false, reason: "El CBU/CVU debe tener exactamente 22 dígitos" };
    return validateCBU(clean);
  }, [form.destination, tab]);

  // SEPA Instant cap: amount > 1000 EUR auto-switches to SEPA Standard
  const numericAmount = parseFloat(form.amount) || 0;
  const overInstantCap = tab === "sepa_instant" && form.currency === "EUR" && numericAmount > SEPA_INSTANT_CAP;
  useEffect(() => {
    if (overInstantCap) {
      toast.info(t("transfers.auto_switch"), { duration: 4000 });
      setTab("sepa_standard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overInstantCap]);

  // Step 1: validate the form and open the confirmation modal ("verify the data").
  const onReview = (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (tab !== "cbu" && (!ibanCheck || !ibanCheck.valid)) {
      setError(ibanCheck?.reason || t("transfers.iban_invalid"));
      return;
    }
    if (tab === "cbu" && (!cbuCheck || !cbuCheck.valid)) {
      setError(cbuCheck?.reason || t("transfers.cbu_invalid"));
      return;
    }
    if (!form.beneficiary_name.trim() || !(parseFloat(form.amount) > 0)) {
      setError(t("transfers.fill_required", { defaultValue: "Completa el beneficiario y el importe." }));
      return;
    }
    if (insufficient) {
      setError(t("transfers.insufficient_funds", {
        defaultValue: `Saldo insuficiente. Disponible: ${fmtMoney(availableBalance, accCurrency)}.`,
        amount: fmtMoney(availableBalance, accCurrency),
      }));
      return;
    }
    setConfirming(true);
  };

  // Step 2: actually submit the transfer after the user confirms.
  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        type: tab,
        beneficiary_name: form.beneficiary_name,
        destination: form.destination,
        amount: parseFloat(form.amount),
        currency: accCurrency,
        destination_country: form.destination_country,
        concept: form.concept,
        account_id: sourceAccount?.id,
      };
      if (tab === "sepa_standard" && form.scheduled_at) payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      const { data } = await api.post("/transfers", payload);
      setResult(data);
      setConfirming(false);
      toast.success(
        data.status === "scheduled" ? t("transfers.toast_scheduled")
        : data.status === "pending" ? t("transfers.toast_pending")
        : t("transfers.toast_executed")
      );
      setForm({ beneficiary_name: "", destination: "", amount: "", concept: "", scheduled_at: "",
               currency: accCurrency, destination_country: form.destination_country });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const bankColor = ibanCheck?.valid ? (BANK_BRANDS[ibanCheck.bank_name] || "#2563EB") : "#2563EB";

  return (
    <div className="space-y-6 max-w-7xl" data-testid="transfers-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">{t("transfers.eyebrow")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("transfers.title")}</h1>
        <p className="text-slate-500 mt-1">{t("transfers.subtitle")}</p>
      </div>

      {/* Tabs neobank pill style */}
      <div className={`grid gap-3 ${TABS.length > 3 ? "grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
        {TABS.map(({ key, label, icon: I, desc }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`text-left p-5 rounded-2xl border transition-all ${
                active
                  ? "text-white shadow-[0_15px_35px_-8px_rgba(124,58,237,0.55)] border-transparent"
                  : "border-slate-200 bg-white text-slate-900 hover:border-purple-300"
              }`}
              style={active ? { background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #2563EB 100%)" } : {}}
            >
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-white/20" : "bg-purple-50 text-purple-600"}`}>
                  <I size={18} />
                </span>
                <span className="font-display font-bold text-base">{label}</span>
              </div>
              <div className={`text-xs mt-2 ${active ? "text-purple-100" : "text-slate-500"}`}>{desc}</div>
            </button>
          );
        })}
      </div>

      {tab === "internal" ? <InternalTransfer /> : (
      <div className="grid lg:grid-cols-3 gap-5">
        <form onSubmit={onReview} className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-7 space-y-5" data-testid="transfer-form">
          <Field label={t("transfers.source_account")}>
            <div className="space-y-2">
              <select value={sourceAccount?.id || ""} onChange={(e) => setSourceId(e.target.value)}
                data-testid="tf-source-account" disabled={accounts.length <= 1}
                className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none disabled:bg-slate-50 disabled:text-slate-700 disabled:cursor-default">
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {(a.label || t(accountTypeKey(a.account_type)))} · {fmtMoney(a.balance, a.currency)}
                  </option>
                ))}
              </select>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-2.5" data-testid="tf-source-balance">
                <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.available_balance", { defaultValue: "Saldo disponible" })}</span>
                <span className="font-mono-fin font-bold text-slate-900" data-testid="tf-source-balance-amount">{fmtMoney(sourceAccount?.balance || 0, accCurrency)}</span>
              </div>
            </div>
          </Field>
          <Field label={t("transfers.beneficiary")}>
            <input required value={form.beneficiary_name} onChange={(e) => set("beneficiary_name", e.target.value)}
              data-testid="tf-beneficiary"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
          </Field>

          <Field label={tab === "cbu" ? t("transfers.destination_cbu") : t("transfers.destination_iban")}
                 hint={tab === "cbu" ? t("transfers.hint_cbu") : t("transfers.hint_iban")}>
            <input required value={form.destination} onChange={(e) => set("destination", e.target.value.toUpperCase())}
              data-testid="tf-destination"
              placeholder={tab === "cbu" ? "0000000000000000000000" : "ES91 2100 0418 4502 0005 1332"}
              className={`w-full px-4 py-3 rounded-xl border font-mono-fin tracking-wider focus:ring-4 outline-none transition
                ${ibanCheck?.valid || cbuCheck?.valid ? "border-green-400 focus:border-green-500 focus:ring-green-100"
                  : (ibanCheck && !ibanCheck.valid) || (cbuCheck && !cbuCheck.valid) ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                  : "border-slate-200 focus:border-blue-600 focus:ring-blue-100"}`} />
          </Field>

          {/* Live IBAN feedback */}
          {tab !== "cbu" && ibanCheck && (
            <div
              data-testid="iban-live-result"
              className={`rounded-2xl p-4 flex items-center gap-4 transition-all ${
                ibanCheck.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              }`}
            >
              {ibanCheck.valid ? (
                <>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
                       style={{ background: bankColor }}>
                    {(displayBank || "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                      <CheckCircle2 size={16} className="text-green-600" />
                      <span>{displayBank}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold ${
                          ibanCheck.network === "SEPA" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                        }`}
                        data-testid="iban-network-badge"
                      >
                        {ibanCheck.network}
                      </span>
                      {displaySource === "openiban.com" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-emerald-100 text-emerald-700" title="Datos del banco verificados contra el registro público de openiban.com">
                          {t("transfers.verified")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-2 flex-wrap" data-testid="iban-meta">
                      <span className="font-semibold">{ibanCheck.country_flag} {ibanCheck.country_name}</span>
                      {displayCity && <><span className="text-slate-300">·</span><span>{displayCity}</span></>}
                      <span className="text-slate-300">·</span>
                      <span className="font-mono-fin">entidad {ibanCheck.bank_code || "—"}</span>
                      {displayBic && <><span className="text-slate-300">·</span><span className="font-mono-fin">BIC {displayBic}</span></>}
                      <span className="text-slate-300">·</span>
                      <span className="font-mono-fin">{groupIban(ibanCheck.iban)}</span>
                    </div>
                  </div>
                  <Building2 size={20} className="text-green-600 shrink-0" />
                </>
              ) : (
                <>
                  <ShieldAlert size={20} className="text-red-600 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-700">{t("transfers.iban_invalid")}</div>
                    <div className="text-xs text-red-600 mt-0.5">{ibanCheck.reason}</div>
                  </div>
                </>
              )}
            </div>
          )}
          {tab === "cbu" && cbuCheck && (
            <div data-testid="cbu-live-result"
              className={`rounded-2xl p-4 flex items-center gap-4 ${cbuCheck.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              {cbuCheck.valid ? (
                <>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-xs shrink-0"
                       style={{ background: BANK_BRANDS[cbuCheck.bank_name] || "linear-gradient(135deg,#0EA5E9,#3B82F6)" }}>
                    {cbuCheck.bank_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                      <CheckCircle2 size={16} className="text-green-600" />
                      <span>{cbuCheck.bank_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold ${
                        cbuCheck.kind === "CVU" ? "bg-cyan-100 text-cyan-700" : "bg-blue-100 text-blue-700"
                      }`}>
                        {cbuCheck.kind}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{cbuCheck.country_flag} {cbuCheck.country_name}</span>
                      <span className="text-slate-300">·</span>
                      <span className="font-mono-fin">entidad {cbuCheck.bank_code}</span>
                      <span className="text-slate-300">·</span>
                      <span className="font-mono-fin">cuenta {cbuCheck.account?.slice(-4) || "—"}</span>
                    </div>
                  </div>
                  <Building2 size={20} className="text-green-600 shrink-0" />
                </>
              ) : (
                <>
                  <ShieldAlert size={20} className="text-red-600 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-700">{t("transfers.cbu_invalid")}</div>
                    <div className="text-xs text-red-600 mt-0.5">{cbuCheck.reason}</div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field
              label={t("transfers.amount")}
              hint={tab === "sepa_instant" ? t("transfers.instant_cap_hint") : null}
            >
              <input required type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                data-testid="tf-amount"
                className={`w-full px-4 py-3 rounded-xl border font-mono-fin focus:ring-4 outline-none transition ${insufficient ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-slate-200 focus:border-blue-600 focus:ring-blue-100"}`} />
              {insufficient && (
                <p className="mt-1.5 text-xs font-semibold text-red-600" data-testid="tf-insufficient-warning">
                  {t("transfers.insufficient_funds", {
                    defaultValue: `Saldo insuficiente. Disponible: ${fmtMoney(availableBalance, accCurrency)}.`,
                    amount: fmtMoney(availableBalance, accCurrency),
                  })}
                </p>
              )}
            </Field>
            <Field label={t("transfers.currency")} hint={t("transfers.currency_account_hint", { defaultValue: "Se usa la moneda de tu cuenta" })}>
              <div data-testid="tf-currency"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 font-mono-fin text-slate-700 flex items-center gap-2">
                <span className="font-bold">{CURRENCIES.find((c) => c.code === accCurrency)?.symbol || accCurrency}</span>
                <span>{accCurrency}</span>
              </div>
            </Field>
            <Field label={t("transfers.destination_country")}>
              <select value={form.destination_country} onChange={(e) => set("destination_country", e.target.value)}
                data-testid="tf-country"
                className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none">
                {COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </Field>
            {tab === "sepa_standard" && (
              <Field label={t("transfers.scheduled_date")}>
                <input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)}
                  data-testid="tf-scheduled"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 font-mono-fin focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
              </Field>
            )}
          </div>

          {fx && (
            <div className="text-sm rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900 flex items-center gap-2" data-testid="tf-fx-preview">
              <ArrowRight size={15} className="text-blue-600 shrink-0" />
              <span>
                {t("transfers.fx_debit")}: <b>{fmtMoney(fx.converted_amount, accCurrency)}</b>
                <span className="text-blue-700"> · 1 {form.currency} = {fx.rate} {accCurrency}</span>
              </span>
            </div>
          )}

          <Field label={t("transfers.concept")}>
            <input value={form.concept} onChange={(e) => set("concept", e.target.value)}
              data-testid="tf-concept" placeholder={t("transfers.concept_placeholder")}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
          </Field>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="tf-error">{error}</div>}

          <button type="submit" disabled={submitting || insufficient}
                  className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_10px_25px_-8px_rgba(124,58,237,0.55)] hover:shadow-[0_14px_30px_-8px_rgba(124,58,237,0.7)] transition-all"
                  style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #2563EB 100%)" }}
                  data-testid="tf-submit">
            {t("transfers.review_btn", { defaultValue: "Revisar y confirmar" })} <ArrowRight size={18} />
          </button>
        </form>

        <aside className="p-7 rounded-3xl space-y-5 text-white glow-purple"
               style={{ background: "linear-gradient(135deg, #0B0F2F 0%, #312E81 30%, #6D28D9 70%, #2563EB 100%)" }}>
          <div className="font-mono-fin text-xs uppercase tracking-widest text-purple-200">{t("transfers.summary")}</div>
          <div className="font-display text-4xl text-white tracking-tighter font-bold">
            {form.amount
              ? `${CURRENCIES.find((c) => c.code === form.currency)?.symbol || ""} ${parseFloat(form.amount).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`
              : `${CURRENCIES.find((c) => c.code === form.currency)?.symbol || ""} 0,00`}
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-slate-300"><span>{t("transfers.rail")}</span><span className="text-white">{TABS.find((tt) => tt.key === tab)?.label}</span></div>
            <div className="flex justify-between text-slate-300"><span>{t("transfers.currency")}</span><span className="text-white font-mono-fin">{form.currency}</span></div>
            <div className="flex justify-between text-slate-300"><span>{t("transfers.destination_country")}</span><span className="text-white">{COUNTRIES.find((c) => c[0] === form.destination_country)?.[1] || form.destination_country}</span></div>
            <div className="flex justify-between text-slate-300"><span>{t("transfers.beneficiary")}</span><span className="text-white truncate ml-3">{form.beneficiary_name || "—"}</span></div>
            <div className="flex justify-between text-slate-300"><span>{tab === "cbu" ? "CBU" : "IBAN"}</span><span className="text-white font-mono-fin text-xs">{form.destination ? groupIban(form.destination) : "—"}</span></div>
            {ibanCheck?.valid && <div className="flex justify-between text-slate-300"><span>Banco</span><span className="text-white text-right max-w-[55%]">{displayBank}</span></div>}
            <div className="flex justify-between text-slate-300"><span>{t("transfers.fee")}</span><span className="text-green-400 font-semibold">{CURRENCIES.find((c) => c.code === form.currency)?.symbol || ""} 0,00</span></div>
          </div>
          {result && (
            <div className="border border-purple-400/40 rounded-xl bg-purple-500/10 p-4 text-xs space-y-1" data-testid="tf-result">
              <div className="font-mono-fin text-purple-200 uppercase tracking-widest">
                {result.status === "scheduled" ? t("transfers.result_scheduled")
                 : result.status === "pending" ? t("transfers.result_pending")
                 : t("transfers.result_executed")}
              </div>
              <div className="text-white font-mono-fin">{result.reference}</div>
            </div>
          )}
        </aside>
      </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3 text-sm text-slate-600">
        <Clock size={18} className="text-blue-600 mt-0.5" />
        <span>{t("transfers.rails_note")}</span>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => !submitting && setConfirming(false)} data-testid="confirm-transfer-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0"><ShieldAlert className="text-amber-600" size={22} /></div>
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">{t("transfers.confirm_title", { defaultValue: "Verifica que los datos son correctos" })}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t("transfers.confirm_sub", { defaultValue: "Revisa los datos antes de enviar. Esta operación no se puede deshacer." })}</p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="confirm-transfer-details">
              {[
                [t("transfers.rail"), TABS.find((tt) => tt.key === tab)?.label],
                [t("transfers.beneficiary"), form.beneficiary_name || "—"],
                [tab === "cbu" ? "CBU" : "IBAN", form.destination ? groupIban(form.destination) : "—"],
                [t("transfers.amount"), `${CURRENCIES.find((c) => c.code === accCurrency)?.symbol || ""} ${parseFloat(form.amount || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} ${accCurrency}`],
                [t("transfers.destination_country"), COUNTRIES.find((c) => c[0] === form.destination_country)?.[1] || form.destination_country],
                [t("transfers.concept"), form.concept || "—"],
                [t("transfers.source_account"), (sourceAccount?.label || t(accountTypeKey(sourceAccount?.account_type))) + " · " + fmtMoney(sourceAccount?.balance || 0, accCurrency)],
              ].map(([k, v], i) => (
                <div key={i} className="flex justify-between gap-3 px-4 py-2.5">
                  <span className="text-slate-500 shrink-0">{k}</span>
                  <span className="text-slate-900 font-medium text-right break-words">{v}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} disabled={submitting} data-testid="confirm-transfer-edit"
                className="btn-secondary text-sm disabled:opacity-50">{t("transfers.confirm_edit", { defaultValue: "Editar" })}</button>
              <button onClick={doSubmit} disabled={submitting} data-testid="confirm-transfer-submit"
                className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #2563EB 100%)" }}>
                {submitting ? t("transfers.processing") : <><CheckCircle2 size={16} /> {t("transfers.confirm_send", { defaultValue: "Confirmar y enviar" })}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
